// Bailey's Playbook (beu-node-v2 docs/bailey-playbook.md): the server reads
// the salon's situation and picks what to do, as one pick and two more
// (A, B, C). Each option says what to make: a campaign from a recipe, a
// setup step, a switch, a journey, a request to the owner. This turns an
// option into a plan in the planner's own shape, so it shows on the same
// card, opens in the same studio and goes through the same approval.
//
// Real things only, as everywhere in the planner: times from the diary,
// reviews word for word, names from the team, the voucher's real price.
// Anything the salon doesn't have is left out, never filled with an example.
import moment from 'moment'
import { RECIPE_FNS, postFor, at, pickSegment, withWindow, first, seasonPlan, planForDay, smsText } from './planner'
import { campaignMoments, momentTemplates } from '../../social/data/seasonal'
import { designPicker } from '../../campaigns/data/story'
import { hrefFor } from '../../../../agent/bailey/skills/knowledge'

const iso = (d) => moment(d).format('YYYY-MM-DD')
// The next working day at h:m that's still ahead (Sundays skipped)
const nextAt = (now, h, m = 0, from = 1) => {
  let d = moment(now).add(from, 'day')
  while (d.day() === 0) d = d.add(1, 'day')
  return at(d, h, m)
}
// A time that's already gone moves to a few minutes from now
const ahead = (t, now) => (moment(t).isAfter(now) ? moment(t) : moment(now).add(20, 'minutes').startOf('minute'))
const I = (ctx, we, i) => (ctx.self ? i : we)
const segOf = (ctx, ids, label) => pickSegment(ctx.segments, ids, label)
// Text and email only when there's someone to send them to, and not when the
// salon runs this person's marketing (the option then asks for posts only)
const sends = (ctx, params) => !ctx.noClients && !(params && Array.isArray(params.channels) && !params.channels.some(c => c === 'email' || c === 'sms'))
const keep = (items, ctx, params) => items.filter(Boolean)
  .filter(i => moment(i.at).isAfter(ctx.now))
  .filter(i => sends(ctx, params) || (i.channel !== 'email' && i.channel !== 'sms'))
  .filter(i => !(ctx.noTexts && i.channel === 'sms'))

// An email in the salon's template for that job (emailFor keeps only the
// header, Bailey's words and the footer)
const email = (id, when, title, trade, kind, e, on = true) => ({ id, channel: 'email', at: when.toISOString(), on, title, email: { ...e, templateId: templateForKind(kind, trade) } })
const TEMPLATE = {
  thanks: { hair: 'all-thank-you', barber: 'all-thank-you', beauty: 'all-thank-you' },
  news: { hair: 'hair-newsletter', barber: 'barber-poster', beauty: 'beauty-newsletter' },
  refer: { hair: 'plain-note', barber: 'barber-refer', beauty: 'plain-note' },
  hours: { hair: 'all-holiday-hours', barber: 'all-holiday-hours', beauty: 'all-holiday-hours' },
  prices: { hair: 'all-price-update', barber: 'all-price-update', beauty: 'all-price-update' },
  rebook: { hair: 'hair-winback', barber: 'barber-rebook', beauty: 'beauty-infill' },
  fill: { hair: 'hair-quiet-days', barber: 'barber-midweek', beauty: 'beauty-nails-offer' },
  welcome: { hair: 'hair-welcome', barber: 'barber-welcome', beauty: 'beauty-welcome' },
  treatment: { hair: 'hair-newsletter', barber: 'barber-poster', beauty: 'beauty-new-treatment' },
  intro: { hair: 'hair-new-stylist', barber: 'barber-new-barber', beauty: 'beauty-newsletter' },
}
const templateForKind = (kind, trade = 'hair') => (TEMPLATE[kind] || TEMPLATE.news)[trade] || TEMPLATE[kind].hair
const text = (id, when, title, lines, ctx) => ({ id, channel: 'sms', at: when.toISOString(), on: true, title, message: smsText(lines, ctx.salonName).message })
const post = (id, when, title, type, ctx, content, extra = {}) => ({ id, channel: 'post', at: when.toISOString(), on: true, title, post: postFor(type, { ...ctx, format: 'post', source: extra.source || null, content, photos: extra.photos, design: extra.design }) })
const story = (id, when, title, type, ctx, content, extra = {}) => ({ id, channel: 'story', at: when.toISOString(), on: true, title, post: postFor(type, { ...ctx, format: 'story', source: extra.source || null, content, photos: extra.photos, design: extra.design }) })
const photo = (ctx, n = 0) => {
  const src = (ctx.photos || [])[n]
  return src ? { main: { src, fx: 0.5, fy: 0.4, zoom: 1 } } : undefined
}
// Free times on a day, from the diary the planner already has
const dayOf = (ctx, date) => (ctx.week || []).find(w => w.date === date) || null
const timesOn = (ctx, date) => ((dayOf(ctx, date) || {}).times || []).slice(0, 3)
const freeDays = (ctx, from, n = 7) => (ctx.week || []).filter(w => moment(w.date).isSameOrAfter(moment(from), 'day') && moment(w.date).isBefore(moment(from).add(n, 'days'), 'day') && w.count >= 1)

// ─── Plans that open a place in Beu (setup, a switch, a journey) ───
// Where each step is done: a place in Bailey's map of the app, so a place
// this person can't open isn't offered (knowledge.js placeFor)
const PLACE = {
  setup: { services: 'services', hours: 'rota', details: 'marketingConfig', card: 'communications', brand: 'brandKit', payments: 'payouts', consent: 'clients', import: 'importData', vouchers: 'vouchers', loyalty: 'loyalty', photos: 'social', deal: 'campaigns' },
  switch: { onlineBooking: 'bookingLink', ownBookingPage: 'yourChoices', marketplace: 'business', deposits: 'services', reviewRequests: 'communications' },
  journey: { birthday: 'journeys', winBack: 'journeys', firstVisit: 'journeys', rebookNudge: 'journeys', aftercare: 'journeys', reviewRequests: 'communications' },
  checklist: { shareLink: 'bookingLink', photos: 'social' },
  show: { overdue: 'clients', lapsed: 'clients', noShows: 'diary' },
  preview: { booking: 'bookingLink' },
}
// The journey recipe each one opens (marketing/journeys/journeyRecipes.js)
const JOURNEY_RECIPE = { birthday: 'birthday', winBack: 'win-back', firstVisit: 'first-visit', rebookNudge: 'rebook', aftercare: 'aftercare' }
const NOTE = {
  services: 'Catalogue › Services. Add what you do and the price, about three minutes.',
  hours: 'Team › Schedules & Hours. Set your working week once and it repeats.',
  details: 'Marketing › Config. Your business name and address go at the bottom of every email.',
  card: 'Add a card for message credits. Posts stay free.',
  brand: 'Your colours, fonts and logo, so everything I make matches.',
  payments: 'Settings › Payouts. About five minutes with Stripe.',
  consent: 'Ask each client at checkout whether they’d like your news. A tick on their profile does it.',
  import: 'Bring your clients over from a file or your old system.',
  vouchers: 'Catalogue › Vouchers. Turn on online sales and it shows on your booking page.',
  loyalty: 'A stamp card or points, with a card designer.',
  onlineBooking: 'Settings › Booking link. Switch online booking on.',
  ownBookingPage: 'Your choices › Booking page.',
  marketplace: 'Show your business in the Beu app, where clients look for someone new.',
  deposits: 'A deposit on longer services, set per service.',
  reviewRequests: 'Ask every client for a review after their visit.',
  shareLink: 'Put your link in your Instagram bio, your WhatsApp status and your Google profile.',
  photos: 'Take three photos of your work this week, with your client’s okay, and add them in Social.',
}

const placePlan = (sc, option, key, area, what) => {
  const h = hrefFor(PLACE[area] && PLACE[area][what])
  if (!h) return null
  const state = area === 'journey' && JOURNEY_RECIPE[what] ? { ...(h.state || {}), openRecipe: JOURNEY_RECIPE[what] } : h.state
  return {
    key, kind: area === 'journey' ? 'winback' : 'setup',
    title: sc.title,
    why: sc.says,
    audience: null,
    items: [],
    action: { label: option.label, path: h.path, ...(state ? { state } : {}), note: NOTE[what] || '' },
    score: 0,
  }
}

// ─── New recipes (Ben's playbook) ───
// Each takes the planner's ctx and the option's params; returns a plan or null.
const R = {}

// A week of posts, every one a different kind (A7, R4, R5, R7)
R.posts = (ctx, p) => {
  const n = p.count === 1 ? 1 : 4
  const pick = designPicker({ family: ctx.family })
  const work = !p.noPhotos && photo(ctx)
  const rv = (ctx.reviews || []).find(x => x && x.rating >= 4 && x.text && x.text.length >= 20 && x.text.length <= 320)
  const kinds = [
    work ? ['freshWork', 'Your work', {}, { photos: work }] : ['tips', 'A tip', {}],
    ['question', 'A question for your followers', {}],
    rv ? ['review', `${first(rv.author || rv.firstName) || 'A client'}’s review`, { quote: rv.text, name: first(rv.author || rv.firstName) || 'A client', service: rv.service || '', rating: rv.rating }, { source: `review:${rv.id}` }] : ['tips', 'Another tip', {}],
    ['announce', 'Book online', { eyebrow: ctx.salonName || 'Book online', headline: 'Your next visit, *sorted*.', body: '', cta: 'Book online, link in bio' }],
  ].slice(0, n)
  const items = kinds.map(([type, title, content, extra = {}], i) => post(`post${i + 1}`, nextAt(ctx.now, i % 2 ? 18 : 12, 30, 1 + i * 2), title, type, ctx, content, { ...extra, design: pick(type) }))
  return { key: `pb:posts:${iso(ctx.now)}`, kind: 'day', title: n === 1 ? 'One post, ready to go' : 'A week of posts', why: n === 1 ? 'Something worth posting, ready when you are.' : 'Four posts, each a different kind, so your feed doesn’t look like an advert.', audience: null, items: keep(items, ctx, p), score: 0 }
}

// Meet me: a new stylist's first fortnight on social (N1, N3, R6)
R.meetMe = (ctx, p) => {
  const name = p.name || first(ctx.me && ctx.me.name) || ''
  const salon = ctx.salonName || ''
  const pick = designPicker({ family: ctx.family })
  const start = nextAt(ctx.now, 12, 30)
  const d = (n) => moment(start).add(n, 'days')
  const sat = moment(start).day(6).isAfter(start) ? moment(start).day(6) : moment(start).day(13)
  const work = photo(ctx)
  const items = [
    post('post1', at(d(0), 12, 30), name ? `New at ${salon}: ${name}` : 'New here', 'team', ctx, { name, ...(salon ? { role: `New at ${salon}` } : {}) }, { design: pick('team'), source: ctx.self ? `team:${ctx.self.id}` : null }),
    story('story1', at(d(0), 18), 'My booking link', 'announce', ctx, { eyebrow: name ? `Book with ${name}` : 'Book online', headline: 'Now *taking bookings*.', body: '', cta: 'Book online, link in bio' }),
    post('post2', at(d(2), 12, 30), 'A tip', 'tips', ctx, {}, { design: pick('tips') }),
    timesOn(ctx, iso(sat)).length ? story('story2', at(moment(sat).subtract(2, 'days'), 8), `${sat.format('dddd')}’s free times`, 'freeSlots', ctx, { date: iso(sat), times: timesOn(ctx, iso(sat)), more: (dayOf(ctx, iso(sat)) || {}).count > 3 }) : null,
    work ? post('post3', at(d(5), 12, 30), 'My work', 'freshWork', ctx, {}, { photos: work, design: pick('freshWork') }) : post('post3', at(d(5), 12, 30), 'A question', 'question', ctx, {}, { design: pick('question') }),
    post('post4', at(d(9), 12, 30), 'Another tip', 'tips', ctx, {}, { design: pick('tips') }),
    story('story3', at(d(11), 8), 'Free times next week', 'announce', ctx, { eyebrow: 'Next week', headline: 'Times free *next week*.', body: '', cta: 'Book online, link in bio' }),
    post('post5', at(d(13), 12, 30), 'Booking now', 'announce', ctx, { eyebrow: salon, headline: name ? `Booking now with *${name}*.` : 'Booking *now*.', body: '', cta: 'Book online, link in bio' }, { design: pick('announce') }),
  ]
  return {
    key: `pb:meetme:${iso(ctx.now)}`, kind: 'intro', title: name ? `Meet ${name}` : 'Introduce yourself',
    why: 'Two weeks on social: who you are, your work, a couple of tips and your free times, each post doing a different job. Free times stay in Stories.',
    audience: null, items: keep(items, ctx, { channels: ['post', 'story'] }), score: 0,
  }
}

// I've moved: tell the clients they brought where to book now (N2)
R.moved = (ctx, p) => {
  const name = p.name || ''
  const salon = ctx.salonName || ''
  const items = [
    email('email', ahead(nextAt(ctx.now, 10), ctx.now), 'I’ve moved email', ctx.trade, 'welcome', {
      subject: `I’ve moved to ${salon}`,
      preheader: 'Same me, new chair. Book online any time.',
      hero: { eyebrow: 'I’ve moved', headline: `You’ll find me at *${salon}* now`, body: `I’m now at ${salon}. Same me, new chair. You can book with me online any time, and see my free times there.`, button: 'Book with me', buttonUrl: '{{BookingLink}}' },
    }),
    text('sms', ahead(nextAt(ctx.now, 18, 0, 1), ctx.now), 'I’ve moved text', [`Hi {{FirstName}}, it’s ${name || 'me'}. I’ve moved to {{SalonName}}, same me, new chair.`, 'Book with me: {{BookingLink}}'], ctx),
    post('post', nextAt(ctx.now, 12, 30), 'I’ve moved', 'announce', ctx, { eyebrow: 'I’ve moved', headline: `Now at *${salon}*.`, body: '', cta: 'Book online, link in bio' }),
    story('story', nextAt(ctx.now, 18), 'My new booking link', 'announce', ctx, { eyebrow: 'New chair, same me', headline: 'Book me *here* now.', body: '', cta: 'Book online, link in bio' }),
  ]
  const plan = { key: `pb:moved:${iso(ctx.now)}`, kind: 'intro', title: 'Tell my clients I’ve moved', why: 'An email and a text to the clients you brought, so they know where to book you now, and a post and a Story with your link.', audience: segOf(ctx, ['all'], 'Your clients'), items: keep(items, ctx, p), score: 0 }
  return p.withMeetMe ? merge(plan, R.meetMe(ctx, p)) : plan
}

// Now taking bookings online (N6, N7, T13, C7)
R.bookOnline = (ctx, p) => {
  const we = I(ctx, 'we', 'I')
  const items = [
    email('email', ahead(nextAt(ctx.now, 10), ctx.now), 'Book online email', ctx.trade, 'news', {
      subject: `Book online with {{SalonName}}`,
      preheader: 'Any time, day or night. No phone call needed.',
      hero: { eyebrow: 'Book online', headline: 'Book *any time* now', body: `You can now book with ${we === 'I' ? 'me' : 'us'} online, day or night. See the free times and pick the one that suits you.`, button: 'See the times', buttonUrl: '{{BookingLink}}' },
    }),
    text('sms', ahead(nextAt(ctx.now, 18), ctx.now), 'Book online text', [`Hi {{FirstName}}, you can now book with {{SalonName}} online, any time.`, 'Book: {{BookingLink}}'], ctx),
    post('post', nextAt(ctx.now, 12, 30), 'Book online', 'announce', ctx, { eyebrow: 'Book online', headline: 'Your next visit, *any time*.', body: p.withQr ? 'Or scan the code at the desk.' : '', cta: 'Book online, link in bio' }),
  ]
  const segment = p.segment === 'served' ? segOf(ctx, ['served', 'all'], 'Clients you’ve looked after') : p.segment === 'regulars' ? segOf(ctx, ['regulars', 'all'], 'Your regulars') : segOf(ctx, ['all'], 'All your clients')
  return { key: `pb:bookonline:${iso(ctx.now)}`, kind: 'fill', title: 'Tell clients they can book online', why: 'An email to everyone, a text to regulars and a post: every online booking is a phone call you don’t have to take.', audience: segment, items: keep(items, ctx, p), score: 0 }
}
R.nowBooking = (ctx, p) => {
  const plan = R.bookOnline(ctx, p)
  if (!plan) return null
  return p.withMeetTeam ? merge({ ...plan, key: `pb:nowbooking:${iso(ctx.now)}`, title: 'Now booking online' }, R.meetTeam(ctx, { weeks: 3 })) : { ...plan, key: `pb:nowbooking:${iso(ctx.now)}`, title: 'Now booking online' }
}

// Launch week for a new solo business (N5)
R.launchWeek = (ctx, p) => {
  const salon = ctx.salonName || ''
  const pick = designPicker({ family: ctx.family })
  const d = (n) => nextAt(ctx.now, 12, 30, 1 + n)
  const work = photo(ctx)
  const items = [
    post('post1', d(0), 'We’re open', 'announce', ctx, { eyebrow: salon, headline: 'Now *open*.', body: '', cta: 'Book online, link in bio' }, { design: pick('announce') }),
    story('story1', at(d(0), 18), 'My booking link', 'announce', ctx, { eyebrow: 'Book online', headline: 'Your first visit, *sorted*.', body: '', cta: 'Book online, link in bio' }),
    work ? post('post2', d(1), 'My work', 'freshWork', ctx, {}, { photos: work, design: pick('freshWork') }) : post('post2', d(1), 'A tip', 'tips', ctx, {}, { design: pick('tips') }),
    post('post3', d(2), 'About me', 'team', ctx, { name: first(ctx.me && ctx.me.name) || '' }, { design: pick('team') }),
    post('post4', d(3), 'A tip', 'tips', ctx, {}, { design: pick('tips') }),
    freeDays(ctx, d(4), 3)[0] ? story('story2', at(freeDays(ctx, d(4), 3)[0].date, 8), 'Free times', 'freeSlots', ctx, { date: freeDays(ctx, d(4), 3)[0].date, times: freeDays(ctx, d(4), 3)[0].times.slice(0, 3) }) : null,
    post('post5', d(5), 'A question', 'question', ctx, {}, { design: pick('question') }),
    post('post6', d(6), 'Last call', 'announce', ctx, { eyebrow: 'Launch week', headline: 'Book your *first visit*.', body: '', cta: 'Book online, link in bio' }, { design: pick('announce') }),
  ]
  return { key: `pb:launch:${iso(ctx.now)}`, kind: 'intro', title: 'Launch week', why: 'Seven days on Instagram: you’re open, your work, about you, a tip, your free times and a last call, each a different job.', audience: null, items: keep(items, ctx, { channels: ['post', 'story'] }), score: 0 }
}

// Meet the team: one person a post, over a few weeks (N7)
R.meetTeam = (ctx, p) => {
  const introduced = new Set((ctx.posts || []).filter(x => x && x.type === 'team').map(x => first(x.content && x.content.name).toLowerCase()))
  const people = (ctx.team || []).filter(t => t && t.name && !introduced.has(first(t.name).toLowerCase())).slice(0, Math.max(1, (p.weeks || 3) * 2))
  if (!people.length) return null
  const items = people.map((t, i) => post(`post${i + 1}`, nextAt(ctx.now, 12, 30, 1 + i * 3), `Meet ${first(t.name)}`, 'team', ctx, { name: first(t.name), ...(t.role ? { role: t.role } : {}) }, { source: `team:${t.id}`, photos: t.image ? { main: { src: t.image, fx: 0.5, fy: 0.35, zoom: 1 } } : undefined }))
  return { key: `pb:meetteam:${iso(ctx.now)}`, kind: 'intro', title: 'Meet the team', why: `One person a post, ${people.length} of them over the next few weeks. People book people.`, audience: null, items: keep(items, ctx, { channels: ['post'] }), score: 0 }
}

// Spotlight one person: their post, their free times, their slots in an email (D5, N8)
R.spotlight = (ctx, p) => {
  const t = (ctx.team || []).find(x => String(x.id) === String(p.staffId)) || { id: p.staffId, name: p.name }
  const name = first(t.name || p.name)
  if (!name) return null
  const base = RECIPE_FNS.intro({ ...ctx, team: [t], posts: [], done: () => false })
  const theirs = (ctx.week || []).filter(w => (w.staffIds || []).map(String).includes(String(p.staffId)) && w.count >= 1).slice(0, 2)
  const extra = theirs.map((w, i) => story(`story${i + 2}`, at(w.date, 8), `${name}’s free times ${moment(w.date).format('dddd')}`, 'freeSlots', ctx, { date: w.date, times: (w.times || []).slice(0, 3), staffName: name, staffId: String(p.staffId) }))
  const items = ((base && base.items) || []).map(i => (i.channel === 'email' ? { ...i, on: true } : i)).concat(extra)
  return { key: `pb:spotlight:${p.staffId}:${iso(ctx.now)}`, kind: 'intro', title: p.intro ? `Introduce ${name}` : `Spotlight ${name}`, why: p.intro ? `${name} is new and has room in their diary. A post, a Story and an email to your clients, then their free times as Stories.` : `${name} has the most room in the next fortnight. Their own post, their free times as Stories and a hello in the next email.`, audience: segOf(ctx, ['all'], 'All your clients'), items: keep(items, ctx, p), score: 0 }
}

// Before I'm away (D8)
R.beforeAway = (ctx, p) => {
  const from = moment(p.from)
  const to = moment(p.to)
  const span = `${from.format('D MMM')} to ${to.format('D MMM')}`
  const items = [
    text('sms', ahead(at(moment(from).subtract(10, 'days'), 18), ctx.now), 'Before I’m away text', [`Hi {{FirstName}}, ${I(ctx, 'we’re', 'I’m')} away ${span}. Book in before then: {{BookingLink}}`], ctx),
    story('story', ahead(at(moment(from).subtract(7, 'days'), 8), ctx.now), 'Before I’m away Story', 'announce', ctx, { eyebrow: `Away ${span}`, headline: 'Book in *before I go*.', body: '', cta: 'Book online, link in bio' }),
    post('post', ahead(at(moment(from).subtract(9, 'days'), 12, 30), ctx.now), 'Before I’m away', 'announce', ctx, { eyebrow: `Away ${span}`, headline: 'Get booked in *before*.', body: '', cta: 'Book online, link in bio' }),
  ]
  return { key: `pb:away:${p.from}`, kind: 'fill', title: 'Before I’m away', why: `You’re away ${span}. A text, a Story and a post so regulars book in before you go. “I’m back” is ready for the day you return.`, audience: segOf(ctx, ['overdue', 'all'], 'Your regulars'), items: keep(items, ctx, p), score: 0 }
}

// I'm back (N11)
R.back = (ctx, p) => {
  const first2 = (ctx.week || []).find(w => w.count >= 1)
  const items = [
    text('sms', ahead(nextAt(ctx.now, 10), ctx.now), 'I’m back text', [`Hi {{FirstName}}, ${I(ctx, 'we’re', 'I’m')} back at {{SalonName}} and taking bookings.`, 'Book yours: {{BookingLink}}'], ctx),
    email('email', ahead(nextAt(ctx.now, 10), ctx.now), 'I’m back email', ctx.trade, 'rebook', {
      subject: `${ctx.self ? 'I’m' : 'We’re'} back`,
      preheader: 'Taking bookings again. Pick a time that suits you.',
      hero: { eyebrow: ctx.self ? 'I’m back' : 'We’re back', headline: 'Taking bookings *again*', body: `${ctx.self ? 'I’m' : 'We’re'} back and the diary’s open. Pick a time that suits you online.`, button: 'See the times', buttonUrl: '{{BookingLink}}' },
    }, false),
    first2 ? story('story', at(first2.date, 8), 'Free times', 'freeSlots', ctx, { date: first2.date, times: (first2.times || []).slice(0, 3), more: first2.count > 3 }) : null,
  ]
  return { key: `pb:back:${iso(ctx.now)}`, kind: 'winback', title: 'Tell my regulars I’m back', why: 'A text to the regulars who are overdue, an email if you want it, and a Story with your free times.', audience: segOf(ctx, ['overdue', 'all'], 'Overdue regulars'), items: keep(items, ctx, p), score: 0 }
}

// Rebook ahead: regulars with nothing booked (D7, T6, N6)
R.rebookAhead = (ctx, p) => {
  const items = [
    text('sms', ahead(nextAt(ctx.now, 18), ctx.now), 'Book ahead text', [`Hi {{FirstName}}, ${I(ctx, 'our', 'my')} diary at {{SalonName}} is filling up. Book your next visit while there’s a choice of times.`, '{{BookingLink}}'], ctx),
    p.channels && p.channels.length === 1 ? null : email('email', ahead(nextAt(ctx.now, 10, 0, 2), ctx.now), 'Book ahead email', ctx.trade, 'rebook', {
      subject: 'Your next visit, before the times go',
      preheader: 'The diary’s filling up. Pick your time now.',
      hero: { eyebrow: 'Book ahead', headline: 'Your next visit, *sorted*', body: `${I(ctx, 'We’re', 'I’m')} busy for the next couple of weeks. Book your next visit now and you’ll get the time you want.`, button: 'Book my next visit', buttonUrl: '{{BookingLink}}' },
    }, false),
  ]
  return { key: `pb:rebookahead:${iso(ctx.now)}`, kind: 'winback', title: 'Rebook regulars ahead', why: 'Regulars with nothing booked get a nudge to book their next visit while there’s a choice of times.', audience: segOf(ctx, ['noUpcoming', 'overdue', 'all'], 'Regulars with nothing booked'), items: keep(items, ctx, p), score: 0 }
}

// A nudge to first-timers who haven't been back (N10)
R.rebookNudge = (ctx, p) => {
  const items = [text('sms', ahead(nextAt(ctx.now, 18), ctx.now), 'Come back text', [`Hi {{FirstName}}, thanks for coming to {{SalonName}}. Ready for your next visit?`, 'Book: {{BookingLink}}'], ctx)]
  return { key: `pb:rebooknudge:${iso(ctx.now)}`, kind: 'winback', title: 'Nudge my first-timers', why: 'A friendly text to first-timers who haven’t booked again yet.', audience: segOf(ctx, ['firstTimers', 'new', 'all'], 'First-timers'), items: keep(items, ctx, p), score: 0 }
}

// The overdue regulars: a personal text and an email (T2, T7, T12)
R.winback = (ctx, p) => {
  const items = [
    text('sms', ahead(nextAt(ctx.now, 18), ctx.now), 'Overdue text', [`Hi {{FirstName}}, it’s been a while since your last visit to {{SalonName}}. Fancy getting booked in?`, '{{BookingLink}}'], ctx),
    email('email', ahead(nextAt(ctx.now, 10, 0, 2), ctx.now), 'Overdue email', ctx.trade, 'rebook', {
      subject: 'It’s been a while',
      preheader: 'Your usual is due. Pick a time that suits you.',
      hero: { eyebrow: 'Due a visit', headline: 'Ready for your *next one*?', body: 'It’s about time for your usual. Pick a time that suits you online.', button: 'Book now', buttonUrl: '{{BookingLink}}' },
    }, false),
  ]
  return { key: `pb:winback:${iso(ctx.now)}`, kind: 'winback', title: 'Nudge overdue regulars', why: 'A personal text to the regulars past their usual gap, and an email if you’d like one too.', audience: segOf(ctx, ['overdue'], 'Overdue regulars'), items: keep(items, ctx, p), score: 0 }
}

// Four weeks of steady filling (T2, T10)
R.fourWeeks = (ctx, p) => {
  const quiet = RECIPE_FNS.quietWeekday(ctx) || RECIPE_FNS.quiet(ctx)
  const nudge = R.winback(ctx, p)
  if (!quiet && !nudge) return null
  const base = quiet || nudge
  const items = ((quiet && quiet.items) || []).concat(((nudge && nudge.items) || []).filter(i => i.channel === 'sms').map(i => ({ ...i, id: 'sms2', title: 'Overdue regulars text' })))
  return { ...base, key: `pb:fourweeks:${iso(ctx.now)}`, title: 'Four weeks of filling', why: 'A post and a Story on your quietest days every week for a month, and a nudge to regulars who are overdue.', items: keep(items, ctx, p) }
}

// Get found: posts for people who don't know you yet (T4)
R.getFound = (ctx, p) => {
  const plan = R.posts(ctx, { count: 3 })
  return plan && { ...plan, key: `pb:getfound:${iso(ctx.now)}`, kind: 'day', title: 'Get found', why: 'Posts that show new people what you do and why you, with your link. Switch on the marketplace too, where clients look for someone new.', items: plan.items.slice(0, 3) }
}

// Add-ons and treatments (T3)
R.addOns = (ctx, p) => {
  const items = [
    post('post1', nextAt(ctx.now, 12, 30), 'Treatments', 'tips', ctx, {}),
    post('post2', nextAt(ctx.now, 12, 30, 3), 'Something extra', 'question', ctx, {}),
    email('email', ahead(nextAt(ctx.now, 10, 0, 1), ctx.now), 'Something extra email', ctx.trade, 'treatment', {
      subject: 'Something extra at your next visit',
      preheader: 'Ask about it when you’re in.',
      hero: { eyebrow: 'At your next visit', headline: 'Something *a little extra*', body: 'A treatment or a finishing touch makes your next visit even better. Ask about it when you’re in, or add it when you book.', button: 'See what’s on', buttonUrl: '{{BookingLink}}' },
    }),
  ]
  return { key: `pb:addons:${iso(ctx.now)}`, kind: 'day', title: 'Promote add-ons', why: 'Two posts on treatments and extras, and an email to clients booked in the next two weeks.', audience: segOf(ctx, ['upcoming', 'all'], 'Clients booked in soon'), items: keep(items, ctx, p), score: 0 }
}
R.productSpotlight = (ctx, p) => {
  const items = [
    post('post', nextAt(ctx.now, 12, 30), 'Product spotlight', 'tips', ctx, {}),
    email('email', ahead(nextAt(ctx.now, 10, 0, 1), ctx.now), 'Ask about it at your visit', ctx.trade, 'news', {
      subject: 'Ask about it at your next visit',
      preheader: 'What we use, and why.',
      hero: { eyebrow: 'What we use', headline: 'Take the *salon* home', body: 'Ask about the products we use when you’re in. They keep it looking like the day you left.', button: 'Book your visit', buttonUrl: '{{BookingLink}}' },
    }, false),
  ]
  return { key: `pb:product:${iso(ctx.now)}`, kind: 'day', title: 'Spotlight a product', why: 'A spotlight on one product at a time helps it sell itself.', audience: segOf(ctx, ['upcoming', 'all'], 'Clients booked in soon'), items: keep(items, ctx, p), score: 0 }
}

// Midweek: weekends full, weekdays empty (D10)
R.midweek = (ctx, p) => {
  const days = freeDays(ctx, moment(ctx.now).add(1, 'day'), 14).filter(w => ![0, 6].includes(moment(w.date).day())).sort((a, b) => b.freeHours - a.freeHours).slice(0, 2)
  const items = [
    email('email', ahead(nextAt(ctx.now, 10), ctx.now), 'Midweek email', ctx.trade, 'fill', {
      subject: 'Easier to get in midweek',
      preheader: 'Plenty of times free Monday to Friday.',
      hero: { eyebrow: 'Midweek', headline: 'Skip the *Saturday rush*', body: `Saturdays go fast, but there’s plenty of room in the week${days[0] ? `, including ${moment(days[0].date).format('dddd')}` : ''}.`, button: 'See the times', buttonUrl: '{{BookingLink}}' },
    }),
    ...days.map((w, i) => story(`story${i + 1}`, at(w.date, 8), `${moment(w.date).format('dddd')}’s free times`, 'freeSlots', ctx, { date: w.date, times: (w.times || []).slice(0, 3), more: w.count > 3 })),
    post('post', nextAt(ctx.now, 12, 30, 2), 'Midweek', 'announce', ctx, { eyebrow: 'Midweek', headline: 'Easier to get in *midweek*.', body: '', cta: 'Book online, link in bio' }),
  ]
  return { key: `pb:midweek:${iso(ctx.now)}`, kind: 'fill', title: 'Promote weekdays', why: 'Saturdays are full and weekdays half empty. An email with weekday times, Stories on the quietest days and a post.', audience: segOf(ctx, ['weekday', 'all'], 'Clients who’ve booked a weekday'), items: keep(items, ctx, p), score: 0 }
}

// A new service (N9)
R.newService = (ctx, p) => {
  const name = p.name || ''
  if (!name) return null
  const items = [
    post('post', nextAt(ctx.now, 12, 30), `New: ${name}`, 'newService', ctx, { eyebrow: 'New', headline: `*${name}*`, body: '' }),
    story('story', nextAt(ctx.now, 18), `${name} Story`, 'newService', ctx, { eyebrow: 'New', headline: `*${name}*`, body: '' }),
    email('email', ahead(nextAt(ctx.now, 10, 0, 1), ctx.now), `${name} email`, ctx.trade, 'treatment', {
      subject: `New at {{SalonName}}: ${name}`,
      preheader: 'Book it online now.',
      hero: { eyebrow: 'New', headline: `Introducing *${name}*`, body: `${name} is new at {{SalonName}}. Book it online now.`, button: `Book ${name}`, buttonUrl: '{{BookingLink}}' },
    }),
  ]
  return { key: `pb:newservice:${p.serviceId || name}`, kind: 'day', title: `Launch ${name}`, why: 'A post, a Story and an email to clients who’d want it.', audience: segOf(ctx, ['all'], 'All your clients'), items: keep(items, ctx, p), score: 0 }
}

// Thank the best clients, first pick of busy dates (C2)
R.thankBest = (ctx, p) => {
  const items = [email('email', ahead(nextAt(ctx.now, 10), ctx.now), 'Thank-you email', ctx.trade, 'thanks', {
    subject: 'A thank-you from {{SalonName}}',
    preheader: 'And first pick of the busy dates.',
    hero: { eyebrow: 'Thank you', headline: 'You’re *one of the best*', body: `Thank you for being one of ${I(ctx, 'our', 'my')} favourite clients. Before the busy dates go to everyone, you get first pick.`, button: 'Pick my time', buttonUrl: '{{BookingLink}}' },
  })]
  return { key: `pb:thankbest:${moment(ctx.now).format('YYYY-MM')}`, kind: 'winback', title: 'Thank my best clients', why: 'A thank-you and first pick of the busy dates goes a long way with the clients who keep you going.', audience: segOf(ctx, ['vip', 'best', 'loyal', 'all'], 'Your best clients'), items: keep(items, ctx, p), score: 0 }
}

// Did you know: regulars who only book one thing (C4)
R.didYouKnow = (ctx, p) => {
  const items = [
    email('email', ahead(nextAt(ctx.now, 10), ctx.now), 'Did you know email', ctx.trade, 'treatment', {
      subject: `Did you know ${I(ctx, 'we', 'I')} do this too?`,
      preheader: 'A little more than you might think.',
      hero: { eyebrow: 'Did you know?', headline: `${I(ctx, 'We', 'I')} do *more* than you think`, body: 'Plenty of clients only ever book one thing. Have a look at everything on the menu next time you book.', button: 'See the menu', buttonUrl: '{{BookingLink}}' },
    }),
    post('post1', nextAt(ctx.now, 12, 30, 1), 'Did you know', 'question', ctx, {}),
    post('post2', nextAt(ctx.now, 12, 30, 4), 'Something else I do', 'tips', ctx, {}),
  ]
  return { key: `pb:didyouknow:${moment(ctx.now).format('YYYY-MM')}`, kind: 'day', title: 'Show them what else I do', why: 'Most regulars only ever book one thing. An email and two posts on everything else.', audience: segOf(ctx, ['oneService', 'regulars', 'all'], 'Regulars who book one thing'), items: keep(items, ctx, p), score: 0 }
}

// Unused gift vouchers (C6)
R.voucherReminder = (ctx, p) => {
  const items = [email('email', ahead(nextAt(ctx.now, 10), ctx.now), 'Voucher reminder', ctx.trade, 'news', {
    subject: 'Your gift voucher is waiting',
    preheader: 'Book your treat any time.',
    hero: { eyebrow: 'A little reminder', headline: 'Your *treat* is waiting', body: 'You’ve a gift voucher with us that hasn’t been used yet. Book your visit any time and bring a friend.', button: 'Book now', buttonUrl: '{{BookingLink}}' },
  })]
  return { key: `pb:voucherreminder:${moment(ctx.now).format('YYYY-MM')}`, kind: 'vouchers', title: 'Remind voucher holders', why: 'Gift vouchers that haven’t been used yet. A reminder brings them in, and they often bring a friend.', audience: segOf(ctx, ['voucherHolders', 'all'], 'Voucher holders'), items: keep(items, ctx, p), score: 0 }
}

// Refer a friend (T1, T4, C8)
R.referFriend = (ctx, p) => {
  const items = [
    email('email', ahead(nextAt(ctx.now, 10), ctx.now), 'Recommend us email', ctx.trade, 'refer', {
      subject: 'Know someone who’d love it here?',
      preheader: 'The people you send are our favourite new clients.',
      hero: { eyebrow: 'Recommend us', headline: 'Share the *love*', body: 'If you’ve a friend who’d love it here, send them our way. The people you send are always our favourite new clients.', button: 'Share the link', buttonUrl: '{{BookingLink}}' },
    }),
    post('post', nextAt(ctx.now, 12, 30, 2), 'Recommend us', 'announce', ctx, { eyebrow: 'Recommend us', headline: 'Tag someone who’s *due a visit*.', body: '', cta: 'Book online, link in bio' }),
  ]
  return { key: `pb:refer:${moment(ctx.now).format('YYYY-MM')}`, kind: 'review', title: 'Ask for recommendations', why: 'People your clients send you are your best new clients. An email and a post.', audience: segOf(ctx, ['all'], 'All your clients'), items: keep(items, ctx, p), score: 0 }
}

// One visit from a reward (A3)
R.rewardNudge = (ctx, p) => {
  const items = [text('sms', ahead(nextAt(ctx.now, 18), ctx.now), 'Nearly there text', ['Hi {{FirstName}}, one more visit to {{SalonName}} and your reward is yours.', 'Book: {{BookingLink}}'], ctx)]
  return { key: `pb:reward:${moment(ctx.now).format('YYYY-MM')}`, kind: 'winback', title: 'Nudge loyalty members', why: 'Members one visit away from their reward. A nudge brings them in this month.', audience: segOf(ctx, ['nearReward', 'loyalty', 'all'], 'One visit from a reward'), items: keep(items, ctx, p), score: 0 }
}

// Christmas or bank holiday hours (D9)
R.hours = (ctx, p) => {
  const from = moment(p.from)
  const to = moment(p.to || p.from)
  const span = to.isSame(from, 'day') ? from.format('dddd D MMMM') : `${from.format('D MMM')} to ${to.format('D MMM')}`
  const items = [
    email('email', ahead(at(moment(from).subtract(10, 'days'), 10), ctx.now), 'Opening hours email', ctx.trade, 'hours', {
      subject: p.closed ? `Closed ${span}` : `Our hours ${span}`,
      preheader: 'Book in before then.',
      hero: { eyebrow: 'Opening hours', headline: p.closed ? `Closed *${span}*` : `Different hours *${span}*`, body: `${p.closed ? `${I(ctx, 'We’re', 'I’m')} closed ${span}.` : `${I(ctx, 'Our', 'My')} hours are different ${span}.`} Book in before then online.`, button: 'Book now', buttonUrl: '{{BookingLink}}' },
    }),
    post('post', ahead(at(moment(from).subtract(7, 'days'), 12, 30), ctx.now), 'Opening hours', 'announce', ctx, { eyebrow: 'Opening hours', headline: p.closed ? `Closed *${span}*.` : `Different hours *${span}*.`, body: '', cta: 'Book online, link in bio' }),
  ]
  return { key: `pb:hours:${p.from}`, kind: 'day', title: 'Let clients know the hours', why: `An email and a post about ${span}, so nobody turns up to a closed door.`, audience: segOf(ctx, ['all'], 'All your clients'), items: keep(items, ctx, p), score: 0 }
}

// A price update, drafted only (T11)
R.priceUpdate = (ctx, p) => {
  const items = [email('email', ahead(nextAt(ctx.now, 10, 0, 7), ctx.now), 'Price update email', ctx.trade, 'prices', {
    subject: 'A small change to our prices',
    preheader: 'Thank you for your support.',
    hero: { eyebrow: 'Prices', headline: 'A small *update*', body: 'From next month a few of our prices are changing. Thank you for your support, it means the world.', button: 'See the menu', buttonUrl: '{{BookingLink}}' },
  }, false)]
  return { key: `pb:prices:${moment(ctx.now).format('YYYY-MM')}`, kind: 'day', title: 'Draft a price update', why: 'A friendly price update, drafted and switched off until you’re ready. Change the words to say exactly what’s changing.', audience: segOf(ctx, ['all'], 'All your clients'), items: keep(items, ctx, p), score: 0 }
}

// A year since opening or joining (S23)
R.anniversary = (ctx, p) => {
  const years = p.years || 1
  const items = [
    email('email', ahead(nextAt(ctx.now, 10), ctx.now), 'Anniversary thank-you', ctx.trade, 'thanks', {
      subject: `${years === 1 ? 'A year' : `${years} years`} of {{SalonName}}`,
      preheader: 'Thank you for every visit.',
      hero: { eyebrow: years === 1 ? 'One year' : `${years} years`, headline: 'Thank *you*', body: `It’s ${years === 1 ? 'a year' : `${years} years`} since ${ctx.self ? 'I started here' : 'we opened on Beu'}. Thank you for every visit.`, button: 'Book your next visit', buttonUrl: '{{BookingLink}}' },
    }),
    post('post', nextAt(ctx.now, 12, 30), 'Anniversary', 'announce', ctx, { eyebrow: years === 1 ? 'One year' : `${years} years`, headline: 'Thank *you*.', body: '', cta: 'Book online, link in bio' }),
  ]
  return { key: `pb:anniversary:${moment(ctx.now).format('YYYY')}`, kind: 'day', title: 'Thank my clients', why: 'A thank-you email and post for the anniversary.', audience: segOf(ctx, ['all'], 'All your clients'), items: keep(items, ctx, p), score: 0 }
}

// A friendly booking policy post (T9)
R.bookingPolicy = (ctx, p) => {
  const items = [post('post', nextAt(ctx.now, 12, 30), 'Booking policy', 'announce', ctx, { eyebrow: 'Good to know', headline: 'Can’t make it? *Just let us know*.', body: 'A little notice means someone else can have your time.', cta: 'Book online, link in bio' })]
  return { key: `pb:policy:${moment(ctx.now).format('YYYY-MM')}`, kind: 'day', title: 'Post my booking policy', why: 'A friendly post about giving notice. Deposits on longer services do the rest.', audience: null, items: keep(items, ctx, { channels: ['post'] }), score: 0 }
}

// Also at: a quiet location in a group (X1)
R.alsoAt = (ctx, p) => R.posts(ctx, { count: 1 })

// ─── The planner's own recipes, pointed at what the option names ───
R.fill = (ctx, p) => {
  let week = ctx.week || []
  if (p.day) week = week.filter(w => w.date === p.day)
  else if (p.weekday) week = week.filter(w => moment(w.date).format('dddd') === p.weekday)
  else if (p.quietestOf) week = week.filter(w => moment(w.date).isBetween(moment(p.quietestOf), moment(p.quietestOf).add(6, 'days'), 'day', '[]')).sort((a, b) => b.freeHours - a.freeHours).slice(0, 1)
  if (p.staffId) week = week.filter(w => !w.staffIds || w.staffIds.map(String).includes(String(p.staffId)))
  return RECIPE_FNS.fill({ ...ctx, week: week.map(w => ({ ...w, freeHours: Math.max(w.freeHours || 0, 3) })), done: () => false })
}
R.day = (ctx, p) => {
  if (p.time && p.day) {
    // One slot just came free: one Story with that time
    const items = [story('story', ahead(moment(ctx.now).add(10, 'minutes'), ctx.now), 'Just came free', 'oneSlot', ctx, { date: p.day, time: p.time })]
    return { key: `pb:freed:${p.day}:${p.time}`, kind: 'fill', title: `${p.time} just came free`, why: 'One Story with the time, while there’s still time to book it.', audience: null, items, score: 0 }
  }
  return planForDay({ day: p.day || moment(ctx.now).add(1, 'day').toDate(), now: ctx.now, trade: ctx.trade, salonName: ctx.salonName, brandColour: ctx.brandColour, family: ctx.family, week: ctx.week, segments: ctx.segments, posts: ctx.posts })
}
R.quiet = (ctx) => RECIPE_FNS.quiet({ ...ctx, done: () => false })
R.quietWeekday = (ctx) => RECIPE_FNS.quietWeekday({ ...ctx, done: () => false })
R.review = (ctx, p) => {
  if (p.ask) {
    // There's no review link to put in an email: the review booster asks
    // every client after their visit (Communications), so that's the ask
    const h = hrefFor('communications')
    return h ? { key: `pb:reviewask:${moment(ctx.now).format('YYYY-MM')}`, kind: 'review', title: 'Ask for reviews', why: 'Switch on review requests and every client is asked after their visit, while it’s fresh.', audience: null, items: [], action: { label: 'Turn on review requests', path: h.path, ...(h.state ? { state: h.state } : {}), note: NOTE.reviewRequests }, score: 0 } : null
  }
  const reviews = p.reviewId ? (ctx.reviews || []).filter(r => String(r.id) === String(p.reviewId)) : ctx.reviews
  return RECIPE_FNS.review({ ...ctx, reviews, done: () => false })
}
R.intro = (ctx) => RECIPE_FNS.intro({ ...ctx, done: () => false })
R.vouchers = (ctx) => RECIPE_FNS.vouchers({ ...ctx, done: () => false })
R.season = (ctx, p) => {
  const find = (list) => list.find(m => m.id === p.moment && (!p.key || m.key === p.key)) || list.find(m => m.id === p.moment)
  let m = find(campaignMoments(ctx.trade, ctx.now, ctx.country, { late: true })) || find(momentTemplates(ctx.trade, ctx.now, ctx.country))
  if (!m) return null
  if (p.weeks) m = { ...m, start: moment.max(moment(m.start), moment(m.end).subtract(p.weeks * 7 - 1, 'days')).toDate() }
  const c = p.bookBefore ? { ...ctx, vouchers: [] } : ctx
  const plan = seasonPlan(m, { ...c, done: () => false })
  return plan && { ...plan, items: keep(plan.items || [], ctx, p) }
}

// Two plans as one (an option that says "Both")
const merge = (a, b) => {
  if (!a) return b
  if (!b) return a
  const ids = new Set(a.items.map(i => i.id))
  const items = a.items.concat(b.items.map(i => (ids.has(i.id) ? { ...i, id: `${i.id}b` } : i)))
  return { ...a, items, why: `${a.why} ${b.why}` }
}

/**
 * One option of a scenario as a plan, in the planner's shape. `scenario` is
 * the server's ({ id, key, title, says, options }), `option` one of its
 * options ({ key, label, action }). null when there's nothing real to make.
 */
export const planForOption = (scenario, option, ctx) => {
  if (!scenario || !option || !option.action) return null
  const a = option.action
  const key = `pb:${scenario.key}:${option.key}`
  let plan = null
  try {
    if (a.kind === 'plan') {
      const fn = R[a.recipe]
      plan = fn ? fn(ctx, a.params || {}) : null
    } else if (['setup', 'switch', 'journey', 'checklist', 'show', 'preview'].includes(a.kind)) {
      const what = a.step || a.setting || a.journey || a.list || a.page
      return placePlan(scenario, option, key, a.kind, what)
    } else if (a.kind === 'request') {
      return { key, kind: 'setup', title: scenario.title, why: scenario.says, audience: null, items: [], action: { label: option.label, request: a.what, note: '' }, score: 0 }
    }
  } catch (e) {
    plan = null
  }
  if (!plan) return null
  // Every plan, the planner's own recipes included, keeps to what this
  // person can send (posts only, no texts when too salesy, dated ahead)
  const items = keep(plan.items || [], ctx, a.params || {})
  if (!(items.length || plan.action)) return null
  return withWindow({ ...plan, items, key })
}

/**
 * The server's pick and alternatives as plans for the cards: each scenario
 * shows its first option that makes something, and carries every option so
 * the card can offer A, B and C.
 */
export const plansFromPlaybook = (pb, ctx) => {
  if (!pb || !pb.pick) return []
  return [pb.pick, ...(pb.alternatives || []), ...(pb.more || [])].filter(Boolean).map((sc) => {
    // Every option that makes something, built now so the card can switch
    // between them without asking again
    const choices = (sc.options || [])
      .filter(o => o.action && o.action.kind !== 'dismiss')
      .map(o => ({ key: o.key, label: o.label, plan: planForOption(sc, o, ctx) }))
      .filter(c => c.plan)
      .map(c => ({ ...c, plan: { ...c.plan, how: c.plan.why, why: sc.says, scenario: { id: sc.id, key: sc.key }, chosen: c.key } }))
    if (!choices.length) return null
    return { ...choices[0].plan, choices: choices.map(({ key, label, plan }) => ({ key, label, plan })) }
  }).filter(Boolean)
}

// ─── Telling the server what happened (not nagging: docs/bailey-playbook.md) ───
const shownThisVisit = new Set()
// The planner's "why not now" reasons, as the playbook's outcomes
const OUTCOME_FOR = { later: 'not-now', 'wrong-day': 'not-now', salesy: 'too-salesy', 'not-for-us': 'not-for-us' }
export const tellPlaybook = (plan, outcome, option) => {
  if (!plan || !plan.scenario || !plan.scenario.key) return
  const key = plan.scenario.key
  if (outcome === 'shown') {
    if (shownThisVisit.has(key)) return
    shownThisVisit.add(key)
  }
  const out = OUTCOME_FOR[outcome] || outcome
  // Best effort: a lost "shown" only means it's offered again sooner
  import('../../../../../_services/agent.service')
    .then(m => m.default.playbookFeedback({ key, outcome: out, ...(option ? { option } : {}) }))
    .catch(() => {})
}

export const RECIPES = Object.keys(R)
